import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import isObject from 'lodash-es/isObject';
import isEmpty from 'lodash-es/isEmpty';
import {fromEvent} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {extractValue} from '../../utils/browser';


/**
 * Chips component, integrating forms, without validation yet
 *
 * Sample usage for template driven forms:
 * dropdown option string
 * ```
 *   <gp-chips [placeholder]="'fill the positions'" [withAutocomplete]="true" [(ngModel)]="position" [modelAsString]="true">
 *     <gp-chips-option *ngFor="let option of ['right', 'left', 'top']" [ngValue]="option">
 *     </gp-chips-option>
 *   </gp-chips>
 * ```
 * dropdown option object
 * ```
 *   <gp-chips [placeholder]="'fill the positions'" [withAutocomplete]="true"
 *   [(ngModel)]="position" [displayBy]="'position'" [identifyBy] ="'id'" >
 *     <gp-chips-option
 *     *ngFor="let option of [{id:1 , position: 'right'}, {id:2 , position: 'left'}, {id:3 , position: 'top'}]"
 *     [ngValue]="option">
 *     </gp-chips-option>
 *   </gp-chips>
 * ```
 * Sample usage for reactive driven forms:
 * ```
 * <div [formGroup]="exampleForm">
 *   <gp-chips [placeholder]="'fill the positions'" [withAutocomplete]="true" [formControlName]="'position'">
 *     <gp-chips-option *ngFor="let option of ['right', 'left', 'top']" [ngValue]="option">
 *     </gp-chips-option>
 *   </gp-chips>
 *  </div>
 * ```
 * Sample usage without autocomplete:
 * ```
 * <div [formGroup]="exampleForm">
 *   <gp-chips [placeholder]="'fill the positions'" [formControlName]="'position'"></gp-chips>
 *  </div>
 * ```
 * Sample usage to be able to add new tags:
 *  Example without auto complete
 * ```
 * <div [formGroup]="exampleForm">
 *   <gp-chips [placeholder]="'fill the positions'" [formControlName]="'position'" [canAddNewTags]="true" ></gp-chips>
 *  </div>
 * ```
 * Example with auto complete
 * <div>
 *  <gp-chips [placeholder]="'fill the positions'" [withAutocomplete]="true" [formControlName]="'position'" [canAddNewTags]="true" >
 *     <gp-chips-option *ngFor="let option of ['right', 'left', 'top']" [ngValue]="option">
 *     </gp-chips-option>
 *   </gp-chips>
 *   </div>
 * ```
 */
export type TagValue = string | { [key: string]: any };

@Component({
  selector: 'gp-chips, gp-chips[ngModel]',
  templateUrl: './chips.component.html',
  styleUrls: ['./chips.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: ChipsComponent
    }
  ]
})
export class ChipsComponent extends Destroyable implements OnInit, AfterViewInit, ControlValueAccessor {

  public extractValue = extractValue;

  @Input()
  public placeholder: string;

  @Input()
  public withAutocomplete: boolean = false;

  @Input()
  public tabindex: number = 0;

  @Input()
  public showTooltip = false;

  @Output()
  public filterOptions: EventEmitter<any> = new EventEmitter<string>();

  @Output()
  public removed: EventEmitter<number> = new EventEmitter<number>();

  @ViewChild('optionsSearch')
  public optionsSearch: ElementRef;

  @Input() set readOnly(readOnly: boolean) {
    this._readOnly = readOnly;
    if (this.form) {
      readOnly ? this.form.get('item').disable() : this.form.get('item').enable();
    }
  }

  get readOnly(): boolean {
    return this._readOnly;
  }

  @Input()
  public canAddNewTags: boolean;

  @Input()
  public modelAsString: boolean;

  @Input()
  public externalInput: boolean;

  @Input()
  public identifyBy: string;

  @Input()
  public displayBy: string;

  public tags: TagValue[] = [];
  public form: UntypedFormGroup;
  public open: boolean = false;
  private _readOnly = false;
  private options: TagValue[] = [];
  private touched = false;

  onChange = (_) => {
    // fake function
  }

  onTouched = () => {
    // fake function
  }


  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      item: new UntypedFormControl({value: '', disabled: this._readOnly})
    });
  }

  ngAfterViewInit(): void {
    if (!this.externalInput) {
      fromEvent(this.optionsSearch.nativeElement, 'keyup')
        .pipe(debounceTime(500),
          takeUntil(this.componentDestroyed$))
        .subscribe(() => {
          this.filterList();
        });
    }
  }

  public remove(index: number) {
    this.markAsTouched();

    this.tags.splice(index, 1);
    this.removed.emit(index);

    this.onChange(this.tags);
  }

  public addValue(value: TagValue) {
    this.markAsTouched();
    this.tags.push(value);
    this.resetFormAndClearFilterOptions();
  }

  private resetFormAndClearFilterOptions() {
    this.form.reset();
    this.filterOptions.emit('');
    this.onChange(this.tags);
  }

  public addNewValue(value: TagValue) {
    if (!this.canAddNewTags) {
      return;
    }
    this.markAsTouched();
    const lowerCaseValue = value.toLowerCase();

    const availableOptions = this.modelAsString
      ? this.options.filter(opt => opt.toLowerCase() === lowerCaseValue)
      : this.options.filter(opt => (this.identifyBy && this.displayBy)
        && (opt[this.identifyBy].toLowerCase() === lowerCaseValue
          || opt[this.displayBy].toLowerCase() === lowerCaseValue));

    this.tags.push(availableOptions.length > 0 ? availableOptions[0] : value);
    this.resetFormAndClearFilterOptions();
  }


  public openOptions(event: boolean) {
    if (this.withAutocomplete) {
      this.open = event;
    }
  }

  public onClickOutside() {
    this.open = false;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: []): void {
    this.tags = Array.isArray(obj) ? obj.filter(value => !isEmpty(value)) : [];
  }

  markAsTouched() {
    if (!this.touched) {
      this.onTouched();
      this.touched = true;
    }
  }

  setDisabledState(disabled: boolean) {
    this._readOnly = disabled;
  }


  public setOption(value: TagValue) {
    if (this.isValid(value, this.options)) {
      this.options.push(value);
    }
  }

  private isValid(value: TagValue, optionList: TagValue[]): boolean {
    if (isObject(value)) {
      return optionList.filter(opt => opt[this.identifyBy] === value[this.identifyBy]).length === 0;
    }

    return optionList.filter(opt => opt === value).length === 0;
  }


  public getDisplayValue(value: TagValue) {
    return isObject(value) ? value[this.displayBy] : value;
  }

  public getTooltipValue(value: TagValue) {
    if (this.showTooltip && value['tooltipText']) {
      return value['tooltipText'];
    }
    if (this.showTooltip) {
      return this.getDisplayValue(value);
    }
    return null;
  }

  public filterList() {
    const searchValue = this.form.get('item').value;
    this.filterOptions.emit(searchValue);
  }

  public hasOptions(): boolean {
    return this.options.length > 0;
  }
}
